import { Link } from "react-router";
import styled from "styled-components";

import useProductEditor from "./useProductEditor";
import useProductSaveAs from "./useProductSaveAs";
import useProductSaver from "./useProductSaver";
import useBIMClass from "../../../../../clients/taxonomy/useBIMClass";
import * as UI from "../../../../../modules/ui-kit";
import useBrand from "../../../clients/brands/useBrand";
import BIMClassSelector from "../../components/entities/bimClass/BIMClassSelector";
import BrandSelector from "../../components/entities/brand/BrandSelector";
import Components from "../../components/form/Components";
import Countries from "../../components/form/Countries";
import Layers from "../../components/form/Layers";
import Materials from "../../components/form/Materials";
import Media from "../../components/form/Media/Media";
import Properties from "../../components/form/Properties";

// "Sorting order": the manufacturer's order of its products within its catalog.
const SORT_ORDER_PARAMETER_ID = "633afb40b2e8cb6b28aa7f1f";

const Styled = styled.main`
    padding: 64px;

    & > nav {
        display: flex;
        flex-direction: column;
        gap: 64px;
        position: fixed;
        width: 192px;

        & > ol {
            list-style: none;
            margin: 0;
            padding: 0;

            a {
                color: #000;
                text-decoration: none;
                transition: 200ms;

                &:hover {
                    color: #999;
                }
            }
        }
    }

    & > form {
        display: grid;
        gap: 128px;
        grid-template-columns: 1fr 3fr;
        grid-auto-flow: row;
        padding-left: 192px;

        & > section {
            display: grid;
            flex-direction: column;
            gap: 32px 64px;
            grid-column: 1 / -1;
            grid-template-columns: subgrid;
            position: relative;

            &:empty {
                display: none;
            }

            aside {
                color: #999;
            }
        }
    }

    .field {
        display: flex;
        flex-direction: column;
        gap: 8px;
        place-items: flex-start;

        aside {
            font-size: 0.8rem;
        }
    }

    .rfs {
        display: grid;
        gap: 16px;
        grid-auto-flow: row;
        grid-template-columns: max-content max-content;

        & > * {
            align-items: center;
            column-gap: inherit;
            display: grid;
            grid-column: 1 / -1;
            grid-template-columns: subgrid;
        }
    }
`;

const SectionTitle = styled.span`
    color: #f2f2f2;
    font-size: 9.6em;
    font-weight: bold;
    height: 0;
    position: absolute;
    top: -0.5em;
    left: 20%;
    z-index: -1;
`;

export default function ProductDetail(props) {
    const { product } = props;

    const productEditor = useProductEditor(product);
    const save = useProductSaver(product, productEditor.raw);
    const saveAs = useProductSaveAs(product);

    const bimClassQuery = useBIMClass(productEditor.reader.bimClass);
    const brand = useBrand(productEditor.reader.brand)?.data?.brand;

    return (
        <Styled>
            <nav>
                <Link to="../..">back</Link>
                <ol>
                    <li>
                        <a href="#identity">Identity</a>
                    </li>
                    <li>
                        <a href="#basics">Basics</a>
                    </li>
                    <li>
                        <a href="#availability">Availability</a>
                    </li>
                    <li>
                        <a href="#media">Media</a>
                    </li>
                    <li>
                        <a href="#bimclass">BIM Class</a>
                    </li>
                    <li>
                        <a href="#components">Components</a>
                    </li>
                    <li>
                        <a href="#layers">Layers</a>
                    </li>
                    <li>
                        <a href="#materials">Materials</a>
                    </li>
                </ol>
                <a
                    href={`https://app.bimproject.cloud/product/${productEditor.reader.id}`}
                    target="_blank" rel="noreferrer"
                >
                    product page
                </a>
            </nav>
            <form onSubmit={(event) => event.preventDefault()}>
                {productEditor.reader.id && (
                    <section>
                        <span />
                        <UI.Field>
                            <span>id</span>
                            <span>{productEditor.reader.id}</span>
                        </UI.Field>
                        <span />
                        <UI.Field>
                            <span>created</span>
                            <span>{productEditor.reader.createdAt}</span>
                        </UI.Field>
                        <span />
                        <UI.Field>
                            <span>updated</span>
                            <span>{productEditor.reader.updatedAt}</span>
                        </UI.Field>
                    </section>
                )}
                <section>
                    <SectionTitle id="identity">Identity</SectionTitle>
                    <span />
                    <UI.Field>
                        <UI.FieldLabel>Brand</UI.FieldLabel>
                        <BrandSelector
                            disabled={!save}
                            onChange={productEditor.writer.brand}
                            value={productEditor.reader.brand}
                        />
                    </UI.Field>
                    <aside>
                        Natural key is your own identifier of the product. Its
                        value is completely arbitrary and is not generated by
                        BIM Project Cloud.
                    </aside>
                    <UI.Field>
                        <UI.FieldLabel>Natural key</UI.FieldLabel>
                        <UI.TextInput
                            disabled={!save}
                            onChange={productEditor.writer.naturalKey}
                            required
                            size={50}
                            style={{ fontFamily: "monospace" }}
                            value={productEditor.reader.naturalKey}
                        />
                    </UI.Field>
                    <aside>
                        Sorting order is the position of this product in its
                        brand&apos;s catalog (1 first; products without one follow,
                        newest first). It is read-only here — change it through a
                        Product IO (XLSX) import.
                    </aside>
                    <UI.Field>
                        <UI.FieldLabel>Sorting order</UI.FieldLabel>
                        <UI.TextInput
                            disabled
                            onChange={() => undefined}
                            readOnly
                            size={10}
                            value={String(
                                productEditor.reader.properties
                                    ?.find((property) => property.parameter?.id === SORT_ORDER_PARAMETER_ID)
                                    ?.value ?? "not set"
                            )}
                        />
                    </UI.Field>
                    <aside>
                        Tags can be used to group products in Categories and
                        Product Lines.
                    </aside>
                    <UI.Field>
                        <UI.FieldLabel>Tags</UI.FieldLabel>
                        <UI.ListInput
                            disabled={!save}
                            label="tag"
                            onChange={productEditor.writer.tags}
                            value={productEditor.reader.tags}
                        />
                    </UI.Field>
                </section>
                <section>
                    <SectionTitle id="basics">Basics</SectionTitle>
                    <aside>
                        When the name is the same in all languages, it is
                        sufficient to fill it in for just one (arbitrary)
                        language
                    </aside>
                    <UI.Field>
                        <UI.FieldLabel>Name</UI.FieldLabel>
                        <UI.TranslatedInput
                            disabled={!save}
                            label="name"
                            onChange={productEditor.writer.name}
                            suggestedLanguages={
                                productEditor.reader.suggestedLanguages
                            }
                            value={productEditor.reader.name}
                        />
                    </UI.Field>
                    <aside>
                        When the about is the same in all languages, it is
                        sufficient to fill it in for just one (arbitrary)
                        language
                    </aside>
                    <UI.Field>
                        <UI.FieldLabel>About</UI.FieldLabel>
                        <UI.TranslatedInput
                            disabled={!save}
                            label="about"
                            onChange={productEditor.writer.about}
                            suggestedLanguages={
                                productEditor.reader.suggestedLanguages
                            }
                            value={productEditor.reader.about}
                        />
                    </UI.Field>
                    <aside>
                        When the material name is the same in all languages, it
                        is sufficient to fill it in for just one (arbitrary)
                        language
                    </aside>
                    <UI.Field>
                        <UI.FieldLabel>Material name</UI.FieldLabel>
                        <UI.TranslatedInput
                            disabled={!save}
                            label="material name"
                            onChange={productEditor.writer.materialName}
                            suggestedLanguages={
                                productEditor.reader.suggestedLanguages
                            }
                            value={productEditor.reader.materialName}
                        />
                    </UI.Field>
                </section>
                <section>
                    <SectionTitle id="availability">Availability</SectionTitle>
                    <span />
                    <div className="rfs">
                        <UI.Field>
                            <UI.FieldLabel>Published</UI.FieldLabel>
                            <UI.SegmentedControl
                                disabled={!save}
                                onChange={productEditor.writer.published}
                                options={[
                                    { label: "Public", value: true },
                                    { label: "Hidden", value: false },
                                ]}
                                value={productEditor.reader.published}
                            />
                        </UI.Field>
                        <UI.Field>
                            <UI.FieldLabel>Archived</UI.FieldLabel>
                            <UI.SegmentedControl
                                disabled={!save}
                                onChange={productEditor.writer.archived}
                                options={[
                                    { label: "Archived", value: true },
                                    { label: "Not archived", value: false },
                                ]}
                                value={productEditor.reader.archived}
                            />
                        </UI.Field>
                        <UI.Field>
                            <UI.FieldLabel>Deleted</UI.FieldLabel>
                            <UI.SegmentedControl
                                disabled={!save}
                                onChange={productEditor.writer.deleted}
                                options={[
                                    { label: "Deleted", value: true },
                                    { label: "Not deleted", value: false },
                                ]}
                                value={productEditor.reader.deleted}
                            />
                        </UI.Field>
                    </div>
                    <span />
                    <UI.Field>
                        <UI.FieldLabel>Countries</UI.FieldLabel>
                        <Countries
                            disabled={!save}
                            onChange={productEditor.writer.countries}
                            value={productEditor.reader.countries}
                        />
                    </UI.Field>
                </section>
                <section>
                    <SectionTitle id="media">Media</SectionTitle>
                    <span />
                    <UI.Field>
                        <Media
                            brandID={brand?.id}
                            disabled={!save}
                            onChange={productEditor.writer.media}
                            value={productEditor.reader.media}
                        />
                    </UI.Field>
                </section>
                <section>
                    <SectionTitle id="bimclass">BIM Class</SectionTitle>
                    <span />
                    <UI.Field>
                        <BIMClassSelector
                            disabled={!save}
                            onChange={productEditor.writer.bimClass}
                            value={productEditor.reader.bimClass}
                        />
                    </UI.Field>
                </section>
                <section>
                    <SectionTitle id="properties">Properties</SectionTitle>
                    <span />
                    <UI.Field>
                        <Properties
                            disabled={!save}
                            lockedParameterIDs={[SORT_ORDER_PARAMETER_ID]}
                            onChange={productEditor.writer.properties}
                            value={productEditor.reader.properties}
                        />
                    </UI.Field>
                </section>
                {bimClassQuery.data?.bimClass?.data?.componentTypes?.length && (
                    <section>
                        <SectionTitle id="components">Components</SectionTitle>
                        <span />
                        <UI.Field>
                            <Components
                                bimClass={productEditor.reader.bimClass}
                                disabled={!save}
                                onChange={productEditor.writer.components}
                                value={productEditor.reader.components}
                            />
                        </UI.Field>
                    </section>
                )}
                {bimClassQuery.data?.bimClass?.data?.type === "structure" && (
                    <section>
                        <SectionTitle id="layers">Layers</SectionTitle>
                        <span />
                        <UI.Field>
                            <Layers
                                bimClass={productEditor.reader.bimClass}
                                disabled={!save}
                                onChange={productEditor.writer.layers}
                                value={productEditor.reader.layers}
                            />
                        </UI.Field>
                    </section>
                )}
                <section>
                    <SectionTitle id="materials">Materials</SectionTitle>
                    <span />
                    <UI.Field>
                        <Materials
                            bimClass={productEditor.reader.bimClass}
                            disabled={!save}
                            onChange={productEditor.writer.materials}
                            value={productEditor.reader.materials}
                        />
                    </UI.Field>
                </section>
                <section>
                    <span />
                    <div style={{ display: "flex", gap: "8px" }}>
                        <UI.Button
                            $appearance="primary"
                            disabled={!save}
                            onClick={save}
                        >
                            Save
                        </UI.Button>
                        <UI.Button disabled={!saveAs} onClick={saveAs}>
                            Save as new Product
                        </UI.Button>
                    </div>
                </section>
            </form>
        </Styled>
    );
}
